import { SetStateAction } from 'react';
import { DDL } from 'types/general.types';
import { Subscription, Tier } from 'types/subscription.types';
import { ProgressBarProps } from 'app/components/Storage/StorageWidget';
import {
  PopupMessageType,
  StorageDetails,
  StorageItem,
  StorageQuotaStatus,
  StorageWidgetProps,
} from './types';

export function bytesToGigabytes(bytes: number): number {
  return parseFloat((Math.abs(bytes ?? null) / 1024 / 1024 / 1024).toFixed(2));
}

export function bytesToMegabytes(bytes: number): number {
  return parseFloat((Math.abs(bytes ?? null) / 1024 / 1024).toFixed(2));
}

export const calcProgressWidth = (progress: number, total: number) => {
  return (100 * progress) / total;
};

export const getPreciseFormattedStorage = (bytes: number) => {
  const MB = bytesToMegabytes(bytes);
  const GB = bytesToGigabytes(bytes);
  const unit = MB > 1023 ? 'GB' : 'MB';
  let formattedValue = MB > 1023 ? GB.toFixed(2) : MB.toFixed(2);
  formattedValue = parseFloat(formattedValue).toString();

  return `${formattedValue}${unit}`;
};

const getUsedStoragePart = (bytes: number, totalMB: number) => {
  const MB = bytesToMegabytes(bytes);
  const GB = bytesToGigabytes(bytes);
  const unit = MB > 1023 ? 'GB' : 'MB';
  return {
    value: MB > 1023 ? GB : MB,
    unit,
    percentage: calcProgressWidth(MB, totalMB),
  };
};

export const prepareStorageData = (
  storage?: StorageDetails
): SetStateAction<StorageWidgetProps | undefined> => {
  if (!storage) return undefined;

  const usedMB = bytesToMegabytes(storage.usedBytes);
  const usedGB = bytesToGigabytes(storage.usedBytes);
  const totalMB = bytesToMegabytes(storage.limitBytes);
  const totalGB = bytesToGigabytes(storage.limitBytes);

  const shouldDisplayUsedProportions =
    storage.freeBytes === 0 && storage.quotaStatus !== 'OverLimit';

  const usedPersonal = getUsedStoragePart(
    storage.usedPersonalBytes,
    shouldDisplayUsedProportions ? usedMB : totalMB
  );
  const usedShared = getUsedStoragePart(
    storage.usedSharedBytes,
    shouldDisplayUsedProportions ? usedMB : totalMB
  );

  return {
    usedUnit: usedMB > 1023 ? 'GB' : 'MB',
    totalUnit: 'GB',
    used: usedMB > 1023 ? usedGB : usedMB,
    total: totalGB,
    usedPersonal,
    usedShared,
    status: storage.quotaStatus,
    freeBytes: storage.freeBytes,
  };
};

export const getTierLabel = (
  tierId: string | null,
  subscriptionTiersDDL?: DDL
) => {
  if (!tierId || !subscriptionTiersDDL) {
    return 'Free';
  }

  const result = subscriptionTiersDDL[tierId];

  if (result) {
    return result.name;
  }

  return tierId;
};

export const getCurrentTierLabel = (
  subscription: Subscription | null,
  subscriptionTiersDDL?: DDL
) => {
  if (!subscription?.hasSubscription || !subscriptionTiersDDL) {
    return 'Free';
  }

  const result = subscriptionTiersDDL[subscription?.tier as string];

  if (result) {
    return result.name;
  }

  return subscription?.tier;
};

export const getStorageLimitText = (
  status: StorageQuotaStatus,
  isOver: boolean
) => {
  if (status === 'OverLimit' || isOver) {
    return 'You are over your storage limit.';
  }
  if (status === 'AlmostOverLimit') {
    return 'Your storage is almost full.';
  }
  return 'Storage';
};

export const getNextTier = (
  tierList: Tier[],
  subscription?: Subscription | null
) => {
  if (!subscription?.hasSubscription) {
    return tierList.find((tier) => !tier.previousTier);
  }
  const currentTier = tierList.find((tier) => tier.tier === subscription?.tier);
  return tierList.find((tier) => tier.tier === currentTier?.nextTier);
};

export const getValues = (
  usedPersonal: StorageItem,
  usedShared: StorageItem,
  isOverLimit: boolean
): ProgressBarProps['values'] => {
  if (isOverLimit) {
    return {
      'var(--chakra-colors-R-0)': 100,
    };
  }
  return {
    [`var(--chakra-colors-B-80)`]: usedPersonal.percentage,
    [`var(--chakra-colors-V-80)`]: usedShared.percentage,
    [`var(--chakra-colors-M-30)`]: 100,
  };
};

export const getTextByType = (tierLabel: string, type?: PopupMessageType) => {
  const storageMessage = `Upgrade to the ${tierLabel} tier to upload music fully, share, and collaborate with others.`;
  const playlistMessage = `To pitch your songs with lyrics and credits included, upgrade to creator Tier.`;

  switch (type) {
    case 'storage':
      return storageMessage;
    case 'playlist':
      return playlistMessage;
    default:
      return storageMessage;
  }
};

export const getStorageFeatures = (messageType?: PopupMessageType) => {
  switch (messageType) {
    case 'playlist':
      return [
        'Pro level share links',
        'Revokable share links',
        'Unlimited Playlists creation',
      ];
    default:
      return [
        'Store all your music in one place',
        'Share and collaborate with others',
        'Release music with confidence',
        'Advanced lyrics features',
      ];
  }
};
